import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { X, Plus, Sparkles, Target, Ban, Palette, MessageSquare, AlertTriangle, ShieldCheck, TrendingUp } from "lucide-react";

/**
 * Sprint 2 Vision AI — Per-render brief editor + live Risk % badge.
 *
 * Renders three chip inputs (must_keep, must_remove, mood) + an optional
 * notes textarea. As the user edits, it debounces + POSTs to
 * /api/vision/risk-score so the badge reflects the current brief.
 *
 * Access: partner / sales / admin. Simple users never see this — the
 * parent DashboardSimulate hides the entire block for them.
 */

const CHIP_MAX = 8;
const CHIP_LEN_MAX = 60;

function ChipInput({ label, icon: Icon, tags, onChange, placeholder, testIdBase, accent = "orange" }) {
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);

  const commit = () => {
    const val = draft.trim().slice(0, CHIP_LEN_MAX);
    if (!val) return;
    if (tags.includes(val)) {
      setDraft("");
      return;
    }
    if (tags.length >= CHIP_MAX) return;
    onChange([...tags, val]);
    setDraft("");
  };

  const remove = (i) => onChange(tags.filter((_, idx) => idx !== i));

  const accentClasses = {
    orange:  { chip: "bg-orange-500/15 text-orange-200 border-orange-500/30", ring: "focus-within:border-orange-500/40" },
    emerald: { chip: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30", ring: "focus-within:border-emerald-500/40" },
    rose:    { chip: "bg-rose-500/15 text-rose-200 border-rose-500/30", ring: "focus-within:border-rose-500/40" },
    violet:  { chip: "bg-violet-500/15 text-violet-200 border-violet-500/30", ring: "focus-within:border-violet-500/40" },
  }[accent];

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} strokeWidth={1.5} className={`text-${accent}-400`} />
        <label className="text-xs uppercase tracking-[0.2em] text-zinc-300 font-semibold">
          {label}
        </label>
        <span className="ml-auto text-[10px] text-zinc-500">{tags.length}/{CHIP_MAX}</span>
      </div>
      <div
        className={`min-h-[52px] rounded-lg border border-white/10 bg-zinc-950/60 px-2.5 py-2 flex flex-wrap gap-1.5 items-center transition ${accentClasses.ring} ${focused ? "shadow-[0_0_0_1px_rgba(238,116,64,0.15)]" : ""}`}
        onClick={(e) => e.currentTarget.querySelector("input")?.focus()}
        data-testid={`${testIdBase}-container`}
      >
        {tags.map((tag, i) => (
          <span
            key={`${tag}-${i}`}
            data-testid={`${testIdBase}-chip-${i}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${accentClasses.chip}`}
          >
            {tag}
            <button
              type="button"
              onClick={() => remove(i)}
              className="ml-0.5 opacity-70 hover:opacity-100"
              aria-label="remove"
              data-testid={`${testIdBase}-remove-${i}`}
            >
              <X size={10} strokeWidth={2} />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, CHIP_LEN_MAX))}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commit();
            }
            if (e.key === "Backspace" && !draft && tags.length) {
              onChange(tags.slice(0, -1));
            }
          }}
          placeholder={tags.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[80px] bg-transparent outline-none text-sm text-white placeholder:text-zinc-500 px-1"
          data-testid={`${testIdBase}-input`}
          disabled={tags.length >= CHIP_MAX}
        />
        {draft && (
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); commit(); }}
            className="text-xs text-orange-400 hover:text-orange-300 px-1"
            data-testid={`${testIdBase}-add`}
          >
            <Plus size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

const RISK_BAND_STYLES = {
  low:    { border: "border-emerald-500/30", bg: "bg-emerald-500/10", text: "text-emerald-300", icon: ShieldCheck },
  medium: { border: "border-amber-500/30",   bg: "bg-amber-500/10",   text: "text-amber-300",   icon: TrendingUp },
  high:   { border: "border-rose-500/30",    bg: "bg-rose-500/10",    text: "text-rose-300",    icon: AlertTriangle },
};

function RiskBadge({ risk, loading }) {
  const { t } = useTranslation();
  const band = risk?.band || "low";
  const style = RISK_BAND_STYLES[band];
  const Icon = style.icon;
  const score = risk?.score ?? "--";
  return (
    <div
      className={`rounded-lg border p-4 ${style.border} ${style.bg}`}
      data-testid="vision-risk-badge"
      data-band={band}
    >
      <div className="flex items-center gap-2">
        <Icon size={16} strokeWidth={1.5} className={style.text} />
        <div className={`text-xs uppercase tracking-[0.2em] font-semibold ${style.text}`}>
          {t("vision_ai.risk_title")}
        </div>
        {loading && (
          <span className="ml-2 text-[10px] text-zinc-500 animate-pulse">…</span>
        )}
        <div className="ml-auto flex items-baseline gap-1">
          <span className={`font-outfit font-black text-2xl ${style.text}`} data-testid="vision-risk-score">
            {score}
          </span>
          <span className="text-xs text-zinc-500">/100</span>
        </div>
      </div>
      <div className="mt-1 text-xs text-zinc-400">
        {t(`vision_ai.risk_band_${band}`)}
      </div>
      {risk?.signals?.length > 0 && (
        <ul className="mt-3 space-y-1" data-testid="vision-risk-signals">
          {risk.signals.slice(0, 5).map((s, i) => (
            <li key={s.label || `signal-${i}`} className="text-[11px] text-zinc-400 flex items-center justify-between gap-2">
              <span className="truncate">{s.label}</span>
              <span className={`shrink-0 tabular-nums font-mono ${s.delta > 0 ? "text-rose-300" : "text-emerald-300"}`}>
                {s.delta > 0 ? "+" : ""}{s.delta}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function RenderBriefEditor({
  value,
  onChange,
  renderMode = "STANDARD",
  lockedCodesCount = 0,
  testId = "vision-brief-editor",
}) {
  const { t } = useTranslation();
  const [risk, setRisk] = useState(null);
  const [loadingRisk, setLoadingRisk] = useState(false);

  const brief = value || { must_keep: [], must_remove: [], mood: [], notes: "" };

  const setField = (k, v) => onChange({ ...brief, [k]: v });

  // Debounced risk-score refresh (400ms after last edit)
  const briefKey = useMemo(
    () =>
      JSON.stringify({
        mk: brief.must_keep,
        mr: brief.must_remove,
        m: brief.mood,
        n: (brief.notes || "").slice(0, 200),
        rm: renderMode,
        lc: lockedCodesCount,
      }),
    [brief, renderMode, lockedCodesCount],
  );

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      setLoadingRisk(true);
      api
        .post("/vision/risk-score", {
          brief: {
            must_keep: brief.must_keep,
            must_remove: brief.must_remove,
            mood: brief.mood,
            notes: brief.notes || null,
          },
          render_mode: renderMode,
          locked_codes_count: lockedCodesCount,
        })
        .then((r) => {
          if (!cancelled) setRisk(r.data);
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) setLoadingRisk(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [briefKey]);

  return (
    <section
      className="mt-8 border border-white/10 rounded-xl bg-zinc-950/40 p-5 sm:p-6"
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-1 flex items-center gap-2">
            <Sparkles size={12} strokeWidth={1.5} />
            {t("vision_ai.brief_eyebrow")}
          </div>
          <h3 className="font-outfit font-bold text-white text-lg tracking-tight">
            {t("vision_ai.brief_title")}
          </h3>
          <p className="text-xs text-zinc-400 mt-1 max-w-xl">
            {t("vision_ai.brief_body")}
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mb-5">
        <ChipInput
          label={t("vision_ai.brief_must_keep")}
          icon={Target}
          tags={brief.must_keep || []}
          onChange={(v) => setField("must_keep", v)}
          placeholder={t("vision_ai.brief_must_keep_placeholder")}
          testIdBase="vision-brief-must-keep"
          accent="emerald"
        />
        <ChipInput
          label={t("vision_ai.brief_must_remove")}
          icon={Ban}
          tags={brief.must_remove || []}
          onChange={(v) => setField("must_remove", v)}
          placeholder={t("vision_ai.brief_must_remove_placeholder")}
          testIdBase="vision-brief-must-remove"
          accent="rose"
        />
        <ChipInput
          label={t("vision_ai.brief_mood")}
          icon={Palette}
          tags={brief.mood || []}
          onChange={(v) => setField("mood", v)}
          placeholder={t("vision_ai.brief_mood_placeholder")}
          testIdBase="vision-brief-mood"
          accent="violet"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare size={14} strokeWidth={1.5} className="text-zinc-400" />
            <label className="text-xs uppercase tracking-[0.2em] text-zinc-300 font-semibold">
              {t("vision_ai.brief_notes")}
            </label>
          </div>
          <textarea
            value={brief.notes || ""}
            onChange={(e) => setField("notes", e.target.value.slice(0, 800))}
            placeholder={t("vision_ai.brief_notes_placeholder")}
            rows={4}
            className="w-full rounded-lg border border-white/10 bg-zinc-950/60 px-3 py-2 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-orange-500/40 resize-none"
            data-testid="vision-brief-notes"
          />
          <div className="text-[10px] text-zinc-500 mt-1 text-right">
            {(brief.notes || "").length}/800
          </div>
        </div>
        <div>
          <RiskBadge risk={risk} loading={loadingRisk} />
        </div>
      </div>
    </section>
  );
}
